<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {Ticket, X} from "lucide-vue-next";
import {__} from "@/i18n/useLanguage.js";
import {useActiveCoupon} from "@/utils/useActiveCoupon.js";

const props = defineProps({
  compact: {
    type: Boolean,
    default: false,
  },
});

const activeCoupon = useActiveCoupon();
const opened = ref(false);
const root = ref(null);
const coupon = computed(() => activeCoupon.coupon);
const panelId = computed(() => props.compact ? "active-coupon-panel-mobile" : "active-coupon-panel-desktop");
const hasProductTargets = computed(() => ["coupon", "order_sale"].includes(coupon.value?.type));
const couponTypeLabel = computed(() => {
  if (coupon.value?.type === "free_shipping") return __("무료배송 쿠폰", "Free shipping coupon");
  if (coupon.value?.type === "order_sale") return __("주문금액 할인 쿠폰", "Order discount coupon");
  return __("상품 할인 쿠폰", "Product discount coupon");
});
const couponName = computed(() => coupon.value
  ? __(coupon.value.name, coupon.value.name_en || coupon.value.name)
  : "");
const saleCouponRoute = {name: "Category", params: {categoryId: "12"}, query: {sale_type: "coupon"}};

watch(coupon, (value) => {
  if (!value) opened.value = false;
});

function closeOnOutsideClick(event) {
  if (root.value && !root.value.contains(event.target)) opened.value = false;
}

function closeOnEscape(event) {
  if (event.key === "Escape") opened.value = false;
}

onMounted(() => {
  document.addEventListener("pointerdown", closeOnOutsideClick);
  document.addEventListener("keydown", closeOnEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", closeOnOutsideClick);
  document.removeEventListener("keydown", closeOnEscape);
});
</script>

<template>
  <div v-if="coupon" ref="root" class="relative flex flex-col items-end">
    <button
      type="button"
      data-testid="active-coupon-launcher"
      class="flex items-center justify-center rounded-full bg-stone-600 text-white shadow-lg transition-colors hover:bg-stone-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-700"
      :class="compact ? 'h-12 w-12' : 'h-14 w-14'"
      :aria-label="__(`사용 중인 쿠폰 보기: ${couponName}`, `View active coupon: ${couponName}`)"
      :aria-expanded="opened"
      :aria-controls="panelId"
      @click="opened = !opened"
    >
      <Ticket class="h-6 w-6" aria-hidden="true" />
      <span class="sr-only">{{ __("쿠폰 사용 중", "Coupon in use") }}</span>
    </button>

    <aside
      v-if="opened"
      :id="panelId"
      role="region"
      :aria-label="__('사용 중인 쿠폰', 'Active coupon')"
      class="absolute bottom-full right-0 mb-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-stone-200 bg-white p-4 text-left text-stone-800 shadow-xl"
    >
      <div class="flex items-start justify-between gap-3">
        <div>
          <h2 class="text-sm font-semibold">{{ __("사용 중인 쿠폰", "Active coupon") }}</h2>
          <p class="mt-1 text-xs text-stone-500">{{ couponTypeLabel }}</p>
        </div>
        <button
          type="button"
          class="rounded p-1 text-stone-500 hover:bg-stone-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-stone-700"
          :aria-label="__('쿠폰 안내 닫기', 'Close coupon information')"
          @click="opened = false"
        >
          <X class="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <p class="mt-3 break-words text-sm font-medium">{{ couponName }}</p>
      <p v-if="coupon.issue_due_date" class="mt-1 text-xs text-stone-500">
        {{ __("사용기한", "Valid until") }}: {{ String(coupon.issue_due_date).slice(0, 10) }}
      </p>
      <router-link
        v-if="hasProductTargets"
        :to="saleCouponRoute"
        class="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-stone-700 px-3 py-2 text-sm font-semibold text-white hover:bg-stone-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-700"
        @click="opened = false"
      >
        {{ __("쿠폰 할인 상품 보러가기", "View coupon discount products") }}
      </router-link>
      <p v-else class="mt-3 text-xs text-stone-600">
        {{ __("무료배송 쿠폰은 상품 할인 목록이 없습니다.", "Free shipping coupons have no discounted product list.") }}
      </p>
    </aside>
  </div>
</template>
